import VpnKeyOutlined from '@mui/icons-material/VpnKeyOutlined';
import { Button, Link, Stack, Typography } from '@mui/material';
import { RestApiKey } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { BreadcrumbMeta } from 'v2/components/Breadcrumb/Breadcrumb';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import EmptyStateOverview from 'v2/components/EmptyStateOverview/EmptyStateOverview';
import useModalContext from 'v2/components/Modal/useModalContext';
import OverviewContent from 'v2/components/OverviewContent/OverviewContent';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useApiKeys from 'v2/hooks/useApiKeys/useApiKeys';
import TableWithData from '../components/TableWithData';
import EditApiKeyDrawer from '../drawers/EditApiKeyDrawer';
import AddApiKeyWizard from '../wizards/AddApiKeyWizard';

const ATLAS_REST_API_DOCS_URL = `${process.env.REACT_APP_ATLAS_REST_API_URL}/docs`;

const Overview: React.FC = () => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const { openModal } = useModalContext();
  const { openDrawer } = useDrawerContext();
  const { restApiKeys, loading, updateApiKey, deleteApiKey } = useApiKeys({
    translation,
    handleFetchError: (msg) => showSnackbar(msg, { severity: 'error' })
  });

  const breadcrumb = {
    title: translation.api_keys,
    crumbs: []
  };

  const hasKeys = restApiKeys.length > 0;

  const handleRowClick = (key: RestApiKey) => {
    openDrawer(
      <EditApiKeyDrawer
        apiKey={key}
        updateApiKey={updateApiKey}
        deleteApiKey={deleteApiKey}
        existingNames={restApiKeys.filter((k) => k.id !== key.id).map((k) => k.description)}
      />
    );
  };

  return (
    <>
      <BreadcrumbMeta breadcrumb={breadcrumb} />
      <Stack spacing={1}>
        <Typography variant="h4">{translation.api_keys}</Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'flex-start' }}
          spacing={2}
          mb={3}
        >
          <Typography
            variant="body1"
            color="textSecondary"
            sx={{ maxWidth: '45em', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {translation.api_keys_description} {translation.api_documentation_link}{' '}
            <Link href={ATLAS_REST_API_DOCS_URL} target="_blank" rel="noopener noreferrer" id="atlas-rest-api-url">
              {ATLAS_REST_API_DOCS_URL}
            </Link>
          </Typography>
          <Button
            id="add-api-key-button"
            variant="contained"
            onClick={() => openModal(<AddApiKeyWizard />, { maxWidth: 760, width: '80vw' })}
            sx={{ flexShrink: 0 }}
          >
            {translation.add_key}
          </Button>
        </Stack>
      </Stack>
      <OverviewContent
        loading={loading}
        hasData={hasKeys}
        hasResults={hasKeys}
        table={<TableWithData restApiKeys={restApiKeys} loading={loading} onRowClick={handleRowClick} />}
        noResults={null} // no search on this page; noResults can never render
        noData={
          <EmptyStateOverview
            icon={VpnKeyOutlined}
            title={translation.no_api_keys_added}
            content={translation.create_api_key_confirmation}
            button={{ label: translation.add_key, onClick: () => openModal(<AddApiKeyWizard />) }}
          />
        }
      />
    </>
  );
};

export default Overview;
